import React from 'react';
import PropTypes from 'prop-types';
import { formatDateTime } from './utils/dateFormatter';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Table,
	TableHeader,
	TableBody,
	TableColumn,
	TableRow,
	TableCell,
	Textarea,
	Button,
	useDisclosure,
	Spinner,
	addToast
} from '@heroui/react';
import { getLocalTimeZone, now } from '@internationalized/date';
import { Edit, Trash2, Plus, Wrench } from 'lucide-react';
import { AppDatePicker } from './components/AppDatePicker';
import { calendarDateTimeToTimestamp, timestampToCalendarDateTime } from './maintenanceDateTime';

// Parse timestamp or date string to Date object
const parseDateTime = value => {
	if (!value) return null;
	if (typeof value === 'number') {
		return new Date(value * 1000);
	}
	if (typeof value === 'string') {
		const num = Number(value);
		if (!isNaN(num) && num > 1000000000) {
			return new Date(num * 1000);
		}
		return new Date(value);
	}
	return null;
};

// Sort events by start time (newest first)
const sortEventsByDate = events => {
	return [...events].sort((a, b) => {
		const dateA = parseDateTime(a.startTime);
		const dateB = parseDateTime(b.startTime);
		if (!dateA || !dateB) return 0;
		return dateB.getTime() - dateA.getTime(); // Descending (newest first)
	});
};

function MaintenanceModal({
	isOpen,
	onClose,
	resource = null,
	maintenanceData = null,
	loading = false,
	onFetch,
	onAdd,
	onSave,
	onDelete
}) {
	// Editing state
	const [editingEvent, setEditingEvent] = React.useState(null); // eventID being edited or null for new
	const [formMode, setFormMode] = React.useState('list'); // 'list' | 'add' | 'edit'

	// Form state - using CalendarDateTime for HeroUI DatePicker
	const [formData, setFormData] = React.useState({
		startTime: null, // CalendarDateTime object
		finishTime: null, // CalendarDateTime object
		comments: ''
	});
	const [errors, setErrors] = React.useState({});
	const [submitting, setSubmitting] = React.useState(false);

	// Delete confirmation
	const deleteConfirmation = useDisclosure();
	const [deletingEventId, setDeletingEventId] = React.useState(null);
	const [isDeleting, setIsDeleting] = React.useState(false);

	// Fetch maintenance data when modal opens
	React.useEffect(() => {
		if (isOpen && resource?.id && onFetch) {
			onFetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, resource?.id]);

	// Reset form and editing state when modal closes
	React.useEffect(() => {
		if (!isOpen) {
			resetForm();
		}
	}, [isOpen]);

	// Get events array safely, sorted by date (newest first)
	const events = React.useMemo(() => {
		const rawEvents = maintenanceData?.events || [];
		return sortEventsByDate(rawEvents);
	}, [maintenanceData?.events]);
	const enforceComments = maintenanceData?.enforceComments || false;

	// Resolve the resource/location timezone. This SAME tz must be used to both
	// HYDRATE the picker on edit and BUILD the epoch on submit — otherwise an
	// edited record double-shifts when the browser tz differs from the resource
	// tz (display renders in one tz, submit reinterprets it as another). Final
	// getLocalTimeZone() fallback keeps hydrate + submit consistent when the data
	// carries no tz.
	const resolveTimezoneId = event =>
		maintenanceData?.defaultEvent?.timeZone?.id ||
		maintenanceData?.defaultEvent?.timeZoneID ||
		event?.timeZone?.id ||
		getLocalTimeZone();

	// Reset form to initial state
	const resetForm = () => {
		setEditingEvent(null);
		setFormMode('list');
		setFormData({
			startTime: null,
			finishTime: null,
			comments: ''
		});
		setErrors({});
	};

	// Handle starting to add a new event
	const handleAddNew = () => {
		// Default to current date/time for new maintenance events
		setFormData({
			startTime: now(getLocalTimeZone()),
			finishTime: now(getLocalTimeZone()),
			comments: ''
		});
		setEditingEvent(null);
		setFormMode('add');
		setErrors({});
	};

	// Handle starting to edit an existing event
	const handleEdit = event => {
		// API returns eventId (camelCase), legacy code used eventID (uppercase D)
		const eventId = event.eventId || event.eventID || event.id;
		if (!eventId) {
			addToast({
				title: 'Error',
				description: 'Cannot edit this event - missing event ID',
				color: 'danger'
			});
			return;
		}

		// Hydrate the picker in the RESOURCE tz (same tz submit uses) so the
		// displayed wall-clock round-trips back to the identical stored epoch.
		const timezoneId = resolveTimezoneId(event);
		setFormData({
			startTime: timestampToCalendarDateTime(event.startTime, timezoneId),
			finishTime: timestampToCalendarDateTime(event.finishTime, timezoneId),
			comments: event.comments || ''
		});
		setEditingEvent(eventId);
		setFormMode('edit');
		setErrors({});
	};

	// Handle form input changes
	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		// Clear error for this field when user starts typing
		if (errors[field]) {
			setErrors(prev => ({ ...prev, [field]: null }));
		}
	};

	// Validate form
	const validate = () => {
		const newErrors = {};

		// Start time is required
		if (!formData.startTime) {
			newErrors.startTime = 'Start time is required';
		}

		// End time is required
		if (!formData.finishTime) {
			newErrors.finishTime = 'End time is required';
		}

		// Start time must be before end time
		if (formData.startTime && formData.finishTime) {
			const startTs = calendarDateTimeToTimestamp(formData.startTime);
			const endTs = calendarDateTimeToTimestamp(formData.finishTime);
			if (startTs >= endTs) {
				newErrors.finishTime = 'End time must be after start time';
			}
		}

		// Comments required if enforceComments is true
		if (enforceComments && (!formData.comments || formData.comments.trim().length === 0)) {
			newErrors.comments = 'Comments are required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle form submission (add or save)
	const handleSubmit = async () => {
		if (!validate()) return;

		// Resolve the resource/location timezone for BOTH add and edit paths via the
		// SAME resolver used to hydrate the picker on edit — so display and submit
		// round-trip through the identical tz. The tz is used to build the epoch AND
		// sent as `timezoneId` (the API forwards it to formatMaintenanceDate).
		const editedEvent = events.find(e => (e.eventId || e.eventID) === editingEvent);
		const timezoneId = resolveTimezoneId(editedEvent);

		const data = {
			startTime: calendarDateTimeToTimestamp(formData.startTime, timezoneId),
			finishTime: calendarDateTimeToTimestamp(formData.finishTime, timezoneId),
			comments: formData.comments.trim(),
			timezoneId: timezoneId
		};

		setSubmitting(true);
		try {
			if (formMode === 'add') {
				await onAdd(data);
				addToast({
					title: 'Success',
					description: 'Maintenance event added successfully',
					color: 'success'
				});
			} else {
				await onSave(editingEvent, data);
				addToast({
					title: 'Success',
					description: 'Maintenance event updated successfully',
					color: 'success'
				});
			}
			// Refresh data and return to list view
			resetForm();
			if (onFetch) {
				onFetch();
			}
		} catch (err) {
			// Handle API error arrays (e.g., { error: ["message"] })
			const errorData = err.response?.data;
			const errorMessage = Array.isArray(errorData?.error)
				? errorData.error[0]
				: errorData?.message || err.message || 'Failed to save maintenance event';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setSubmitting(false);
		}
	};

	// Handle cancel form - return to list view
	const handleCancelForm = () => {
		resetForm();
	};

	// Handle initiating delete - open confirmation
	const handleDeleteClick = eventID => {
		setDeletingEventId(eventID);
		deleteConfirmation.onOpen();
	};

	// Handle confirmed delete
	const handleConfirmDelete = async () => {
		if (!deletingEventId) return;

		setIsDeleting(true);
		try {
			await onDelete(deletingEventId);
			addToast({
				title: 'Success',
				description: 'Maintenance event deleted successfully',
				color: 'success'
			});
			deleteConfirmation.onClose();
			setDeletingEventId(null);
			// Refresh data
			if (onFetch) {
				onFetch();
			}
		} catch (err) {
			// Handle API error arrays (e.g., { error: ["message"] })
			const errorData = err.response?.data;
			const errorMessage = Array.isArray(errorData?.error)
				? errorData.error[0]
				: errorData?.message || err.message || 'Failed to delete maintenance event';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setIsDeleting(false);
		}
	};

	// Handle cancel delete
	const handleCancelDelete = () => {
		deleteConfirmation.onClose();
		setDeletingEventId(null);
	};

	// Handle modal close
	const handleClose = () => {
		resetForm();
		onClose();
	};

	return (
		<>
			{/* Main Maintenance Modal */}
			<Modal isOpen={isOpen} onClose={handleClose} size="3xl" scrollBehavior="inside">
				<ModalContent>
					<ModalHeader className="flex items-center gap-2">
						<Wrench className="w-5 h-5" />
						Resource Maintenance: {resource?.name}
					</ModalHeader>
					<ModalBody>
						{loading ? (
							<div className="flex justify-center py-8">
								<Spinner />
							</div>
						) : formMode === 'list' ? (
							<>
								{/* Events List */}
								{events.length === 0 ? (
									<div className="text-center py-8 text-default-500">
										No maintenance events scheduled for this resource.
									</div>
								) : (
									// removeWrapper kills HeroUI's overflow slot and the pinned
									// number/actions columns floor this at ~600px — fence it so a
									// phone scrolls the table, not the modal body.
									<div className="overflow-x-auto">
									<Table
										aria-label="Maintenance events"
										removeWrapper
										classNames={{
											tbody:
												'[&>tr:nth-child(even)>td]:bg-default-100 [&>tr:nth-child(even)>td:first-child]:rounded-l-lg [&>tr:nth-child(even)>td:last-child]:rounded-r-lg'
										}}
									>
										<TableHeader>
											<TableColumn key="number" width={50}>
												#
											</TableColumn>
											<TableColumn key="startTime">MAINTENANCE STARTS</TableColumn>
											<TableColumn key="finishTime">MAINTENANCE ENDS</TableColumn>
											<TableColumn key="comments">COMMENTS</TableColumn>
											<TableColumn key="actions" align="end" width={100}>
												ACTIONS
											</TableColumn>
										</TableHeader>
										<TableBody
											items={events.map((e, i) => ({
												...e,
												_index: i,
												_key: e.eventId || e.eventID || e.id || `event-${i}`
											}))}
										>
											{event => (
												<TableRow key={event._key}>
													<TableCell>{event._index + 1}</TableCell>
													<TableCell>{formatDateTime(event.startTime)}</TableCell>
													<TableCell>{formatDateTime(event.finishTime)}</TableCell>
													<TableCell>{event.comments || '-'}</TableCell>
													<TableCell>
														<div className="flex gap-1 justify-end">
															<Button
																isIconOnly
																size="sm"
																variant="light"
																color="primary"
																onPress={() => handleEdit(event)}
																aria-label="Edit maintenance event"
															>
																<Edit className="w-4 h-4" />
															</Button>
															<Button
																isIconOnly
																size="sm"
																variant="light"
																color="danger"
																onPress={() =>
																	handleDeleteClick(event.eventId || event.eventID || event.id)
																}
																aria-label="Delete maintenance event"
															>
																<Trash2 className="w-4 h-4" />
															</Button>
														</div>
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
									</div>
								)}
							</>
						) : (
							<>
								{/* Add/Edit Form */}
								<div className="space-y-4">
									<h3 className="text-lg font-semibold">
										{formMode === 'add' ? 'Add New Maintenance Event' : 'Edit Maintenance Event'}
									</h3>

									{/* Start Time */}
									<AppDatePicker
										label="Start Date/Time"
										granularity="minute"
										hourCycle="h23"
										hideTimeZone
										placeholderValue={now(getLocalTimeZone())}
										value={formData.startTime}
										onChange={value => handleInputChange('startTime', value)}
										isInvalid={!!errors.startTime}
										errorMessage={errors.startTime}
										isRequired
									/>

									{/* End Time */}
									<AppDatePicker
										label="End Date/Time"
										granularity="minute"
										hourCycle="h23"
										hideTimeZone
										placeholderValue={now(getLocalTimeZone())}
										value={formData.finishTime}
										onChange={value => handleInputChange('finishTime', value)}
										isInvalid={!!errors.finishTime}
										errorMessage={errors.finishTime}
										isRequired
									/>

									{/* Comments */}
									<Textarea
										label="Comments"
										placeholder="Enter comments about this maintenance event"
										value={formData.comments}
										onValueChange={value => handleInputChange('comments', value)}
										isInvalid={!!errors.comments}
										errorMessage={errors.comments}
										isRequired={enforceComments}
										minRows={3}
									/>

									{enforceComments && (
										<p className="text-sm text-default-500">
											* Comments are required by your organization settings
										</p>
									)}
								</div>
							</>
						)}
					</ModalBody>
					<ModalFooter>
						{formMode === 'list' ? (
							<>
								<Button variant="light" onPress={handleClose}>
									Close
								</Button>
								<Button
									color="primary"
									startContent={<Plus className="w-4 h-4" />}
									onPress={handleAddNew}
								>
									Add Maintenance
								</Button>
							</>
						) : (
							<>
								<Button variant="light" onPress={handleCancelForm} isDisabled={submitting}>
									Cancel
								</Button>
								<Button color="primary" onPress={handleSubmit} isLoading={submitting}>
									{formMode === 'add' ? 'Add Maintenance' : 'Save'}
								</Button>
							</>
						)}
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Delete Confirmation Modal */}
			<Modal
				scrollBehavior="inside"
				isOpen={deleteConfirmation.isOpen}
				onClose={handleCancelDelete}
				size="sm"
			>
				<ModalContent>
					<ModalHeader className="flex flex-col gap-1">Confirm Delete</ModalHeader>
					<ModalBody>
						<p>Are you sure you want to delete this maintenance event?</p>
						<p className="text-default-500 text-sm">This action cannot be undone.</p>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={handleCancelDelete} isDisabled={isDeleting}>
							Cancel
						</Button>
						<Button color="danger" onPress={handleConfirmDelete} isLoading={isDeleting}>
							Delete
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}

MaintenanceModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	resource: PropTypes.object,
	maintenanceData: PropTypes.shape({
		events: PropTypes.array,
		defaultEvent: PropTypes.object,
		enforceComments: PropTypes.bool
	}),
	loading: PropTypes.bool,
	onFetch: PropTypes.func.isRequired,
	onAdd: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired
};

export default MaintenanceModal;
